import { useSEO } from "../hooks/useSEO";
import { PolicyShell, H, BUSINESS } from "./_policyShell";

const Complaints = () => {
  useSEO({
    title: "Complaints Procedure",
    description:
      "Sterling Bio Ltd complaints procedure. We aim to resolve every complaint within 5 UK business days. Escalation route includes Faster Payments bank recall and Citizens Advice.",
    path: "/complaints",
  });
  return (
    <PolicyShell kicker="Legal" title="Complaints Procedure" testid="complaints-page">
      <p className="text-sm text-[#64748B] font-mono uppercase tracking-wider">
        Last updated: 25 February 2026
      </p>
      <p>
        We want every Sterling Bio customer to be satisfied with their purchase. If something has
        gone wrong, please use the procedure below — we treat every complaint seriously and aim
        to resolve them quickly.
      </p>

      <H>1. How to raise a complaint</H>
      <p>Contact us by any of the following channels with your order number and a brief description:</p>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li>
          Email:{" "}
          <a className="text-[#2C8C9C] hover:underline" href={`mailto:${BUSINESS.email}`}>
            {BUSINESS.email}
          </a>
        </li>
        <li>
          Telephone:{" "}
          <a className="text-[#2C8C9C] hover:underline" href={`tel:${BUSINESS.phone}`}>
            {BUSINESS.phoneDisplay}
          </a>{" "}
          (09:00–17:00 GMT, Mon–Fri)
        </li>
        <li>
          Postal address: {BUSINESS.name}, {BUSINESS.address}
        </li>
      </ul>

      <H>2. Our service levels</H>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li>
          <strong>Acknowledgement</strong> — within <strong>1 UK business day</strong>.
        </li>
        <li>
          <strong>Initial response</strong> — within <strong>3 UK business days</strong>,
          including any clarifying questions.
        </li>
        <li>
          <strong>Resolution target</strong> — within <strong>5 UK business days</strong> of
          acknowledgement. Complex cases (e.g. carrier investigation) may take longer; we will
          keep you updated every 3 days.
        </li>
      </ul>

      <H>3. What we will need from you</H>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li>Your order number (begins <code>SB-</code>) and the email used to place the order.</li>
        <li>A description of the issue, with dates/times where relevant.</li>
        <li>Photographs if the complaint concerns damage, packaging or incorrect items.</li>
        <li>Your preferred resolution (refund, replacement, etc.).</li>
      </ul>

      <H>4. Escalation</H>
      <p>
        If you are not satisfied with our resolution, you may escalate as follows:
      </p>
      <ul className="list-disc list-outside pl-6 space-y-2">
        <li>
          <strong>Bank transfer / Pay-by-Bank payments</strong> — because all our payments are
          taken as UK bank transfers (via Wallid Pay-by-Bank / Open Banking), your first step is
          to contact your bank. Under the UK Faster Payments scheme rules banks may assist with
          recall of an unauthorised or misdirected payment.
        </li>
        <li>
          <strong>General consumer advice</strong> — Citizens Advice (UK) on 0808 223 1133 or via{" "}
          <a
            className="text-[#2C8C9C] hover:underline"
            href="https://www.citizensadvice.org.uk/consumer/"
            target="_blank"
            rel="noopener noreferrer"
          >
            citizensadvice.org.uk
          </a>
          .
        </li>
        <li>
          <strong>Online disputes (ODR)</strong> — EU residents may use the European Commission
          ODR platform if applicable.
        </li>
      </ul>

      <H>5. Record keeping</H>
      <p>
        We keep records of every complaint for a minimum of 2 years to identify trends and
        improve our service. Personal data within those records is handled under our{" "}
        <a className="text-[#2C8C9C] hover:underline" href="/privacy-policy">
          Privacy Policy
        </a>{" "}
        and the UK GDPR.
      </p>

      <H>6. Merchant identity</H>
      <p>
        Complaints should be addressed to <strong>{BUSINESS.name}</strong> (Company No.{" "}
        {BUSINESS.companyNo}, registered in {BUSINESS.registeredIn}). Trading address:{" "}
        {BUSINESS.address}.
      </p>
    </PolicyShell>
  );
};

export default Complaints;
